import { uuid } from '@primer-io/sdk-utils/uuid';
import { SdkError } from '@primer-io/sdk-utils/errors/errors';
import type {
  PrimerFetchResponse,
  PrimerRequestInit,
  RetryBackoff,
  RetryPolicy,
} from '@primer-io/sdk-utils/request';
import { Step, StepHandler, StepResult } from '../types';
import {
  HttpRequestParams,
  HttpResponseOutput,
  HttpRetryPolicy,
  HttpTransport,
} from './types';

type HttpRequestStep = Step<HttpRequestParams>;

/** Defaults for the params a step leaves out. */
const DEFAULT_TIMEOUT_MS = 30_000;
const DEFAULT_MAX_ATTEMPTS = 1;
const DEFAULT_BACKOFF: RetryBackoff = 'exponential';
const DEFAULT_BASE_DELAY_MS = 250;
/** Explicit statuses only — sdk-utils would otherwise retry every 5xx. */
const DEFAULT_RETRY_ON: readonly number[] = [];

const METHODS: readonly string[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

function assertValidParams(params: HttpRequestParams): void {
  if (!METHODS.includes(params.method)) {
    throw new SdkError({
      origin: 'primer',
      message: `http.request method must be one of ${METHODS.join(', ')}; received ${JSON.stringify(params.method)}.`,
    });
  }
  if (params.method === 'GET' && params.body != null) {
    throw new SdkError({
      origin: 'primer',
      message: 'http.request cannot send a body with a GET.',
    });
  }
}

/**
 * Executes a `http.request` step through the host SDK's request layer. This
 * handler only translates: step params become a `PrimerRequestInit` (retry
 * policy included — sdk-utils runs the actual retry loop), and the settled
 * response becomes step data whatever its status, so the state processor stores
 * it at the step's `outputPath` and CEL can read `status`, `success`, `headers`
 * and `body`. A request that never got a response throws, which the Orchestrator
 * reports as the `error` outcome.
 */
export class HttpRequestStepHandler implements StepHandler<HttpRequestStep> {
  static readonly type = 'http.request';
  static readonly version = '1.0.0';

  private abortController = new AbortController();

  constructor(private transport: HttpTransport) {}

  async execute(step: HttpRequestStep): Promise<StepResult> {
    assertValidParams(step.params);

    // One id per execution, reused across retries: it becomes the X-Request-Id
    // the backend logs, and the handle `transport.cancel` aborts by.
    const id = uuid();
    const init = toRequestInit(step.params, id);
    const signal = this.abortController.signal;

    // Direct transports honor the signal themselves; iframe-routed ones can
    // only be reached by id.
    const cancelInFlight = () => this.transport.cancel?.(id);
    signal.addEventListener('abort', cancelInFlight, { once: true });

    try {
      const response = await this.transport.request(step.params.url, init, {
        signal,
      });

      if (response.status == null) {
        // No response at all — nothing for the schema to route on.
        throw response.error;
      }

      return {
        outcome:
          response.status >= 200 && response.status < 300 ? 'success' : 'error',
        data: toOutput(response),
      };
    } finally {
      signal.removeEventListener('abort', cancelInFlight);
    }
  }

  /** Drops any request still in flight when the flow ends. */
  onFinish() {
    this.abortController.abort();
  }
}

function toRequestInit(
  params: HttpRequestParams,
  id: string,
): PrimerRequestInit {
  const { method, body, timeoutMs, retry, idempotencyKey, keepalive } = params;
  const hasBody = body != null;

  const headers: Record<string, string> = {};
  // Idempotency is schema-opt-in: the key is sent verbatim when the step
  // provides one, and nothing is generated otherwise — the backend does not
  // handle keys it did not ask for yet.
  if (idempotencyKey) {
    headers['X-Idempotency-Key'] = idempotencyKey;
  }
  if (hasBody) {
    headers['Content-Type'] = 'application/json';
  }

  return {
    method,
    id,
    headers,
    body: hasBody ? JSON.stringify(body) : undefined,
    timeoutMs: timeoutMs ?? DEFAULT_TIMEOUT_MS,
    keepalive: keepalive || undefined,
    retry: toRetryPolicy(retry),
  };
}

function toRetryPolicy(
  retry: HttpRetryPolicy | undefined,
): RetryPolicy | undefined {
  if (!retry) {
    // No policy means one attempt — sdk-utils' default of four is for its own
    // typed endpoints, not for schema-driven steps.
    return undefined;
  }

  return {
    maxAttempts: Math.max(retry.maxAttempts ?? DEFAULT_MAX_ATTEMPTS, 1),
    backoff: retry.backoff ?? DEFAULT_BACKOFF,
    baseDelayMs: retry.baseDelayMs ?? DEFAULT_BASE_DELAY_MS,
    retryOn: retry.retryOn ?? [...DEFAULT_RETRY_ON],
  };
}

/**
 * The schema reads the response from the step's `outputPath` either way, but the
 * outcome is the only thing its `next` map can route on — so a failed request
 * has to report `error`.
 */
function toOutput(response: PrimerFetchResponse<unknown>): HttpResponseOutput {
  const headers: Record<string, string> = {};
  for (const [key, value] of Object.entries(response.headers ?? {})) {
    if (value != null) {
      headers[key] = value;
    }
  }

  return {
    status: response.status as number,
    success:
      (response.status as number) >= 200 && (response.status as number) < 300,
    headers,
    body: response.data ?? response.error?.response?.data ?? null,
  };
}
